"use client";

import { METRICS_EVENTS } from "@cede/types";
import { DISCORD_INVITE_URL } from "@cede/utils";
import { SocialLinks } from "@/components/icons/compositions/SocialLinks";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";

const ADMIN_TELEGRAM_URL = "https://t.me/TheCapitalOfAu";

export const Footer = () => {
  const router = useRouter();
  const { isTMA } = useTelegramContext();
  const { trackEvent } = useMetricsEvents();

  const handleReportBotClick = () => {
    // Track when user clicks report bots button in footer
    trackEvent(METRICS_EVENTS.REPORT_BOTS_BUTTON_CLICK, {});
    window.open(ADMIN_TELEGRAM_URL, "_blank");
  };

  // Hide footer on TMA and desktop (since sidebar has footer content)
  // Only show on tablet (md) if sidebar is not visible
  if (isTMA) {
    return null;
  }

  return (
    <footer
      className={cn(
        "relative bottom-0 w-full shrink-0",
        "hidden", // Hide on all screens since sidebar now has footer content
        "flex-col md:flex-row",
        "items-center justify-between",
        "gap-[62px] md:gap-8 lg:gap-12",
        "p-[30px] md:p-6 lg:p-9",
        "rounded-medium md:rounded-medium lg:rounded-large",
        "bg-cede-bg-component border border-cede-border-container",
      )}
    >
      {/* Combined Actions Section */}
      <div className={cn("flex flex-1 flex-col gap-2", "items-center text-center md:items-start md:text-left")}>
        <h6 className="h6 mb-1 font-semibold text-white">Help Us Improve</h6>

        {/* Action Buttons Row */}
        <div className={cn("flex w-full max-w-[400px] gap-2", "flex-col md:flex-row")}>
          <Button
            variant="tertiary"
            size="medium"
            onClick={() => window.open(DISCORD_INVITE_URL)}
            className="min-w-40 flex-1"
          >
            <span className="mr-2">💬</span>
            Send Feedback
          </Button>
          <Button variant="secondary" size="medium" onClick={handleReportBotClick} className="min-w-[160px] flex-1">
            <span className="mr-2">🤖</span>
            Report Bots
          </Button>
        </div>

        <p className="body2 mt-1 max-w-[400px] text-cede-text-secondary">
          Report bugs, share feedback, or help keep trading fair by reporting suspicious activity
        </p>
      </div>

      {/* Social & Legal Section */}
      <div className={cn("flex min-h-[120px] flex-col justify-between gap-3", "items-center md:items-end")}>
        <div className={cn("flex items-center gap-2", "flex-col md:flex-row")}>
          <p className="body1">Follow us</p>
          <SocialLinks size={20} variant="primary" interactive />
        </div>

        <div className={cn("flex flex-col gap-1", "items-center md:items-end")}>
          <Button
            variant="transparent"
            size="medium"
            onClick={() => router.push(ROUTES.PRIVACY_POLICY)}
            className="text-sm"
          >
            Privacy Policy
          </Button>
          <Button
            variant="transparent"
            size="medium"
            onClick={() => router.push(ROUTES.TERMS_AND_CONDITIONS)}
            className="text-sm"
          >
            Terms and Conditions
          </Button>
        </div>
      </div>
    </footer>
  );
};
